import { useEffect, useState, useRef } from 'react';
import { formatPhoneNumber } from '../../utils/phoneUtils';
import {
  Dialog, DialogTitle, DialogContent, Paper,
  TextField, Button, FormControl, InputLabel, Select, MenuItem,
  Box, Typography, Switch, FormControlLabel, IconButton, Stack,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import { createUser, updateUser, getAgentDetailsByAgentId, getCommissionHistory, adminResetPassword, setUserProcessingFee } from '../../api/userApi';
import { getActiveBuddies, getBuddyHistory, saveBuddies, chainHasBuddySplit } from '../../api/userBuddyApi';
import { getManagerAssignments, createManagerAssignment, expireManagerAssignment } from '../../api/managerAssignmentApi';
import { getTiers, getUserTierStatus } from '../../api/tierApi';
import { getSalesTitle } from '../../api/formGeneratorApi';
import { adminUserStatus } from '../../api/quizApi';
import { useUser } from '../../context/userContext';
import BankSelect from '../common/bankSelect';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { Collapse } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const EXCLUDED_BUDDY_ROLES = ['Group Admin', 'Admin', 'Astern Admin', 'Super Admin', 'Viewer'];

const ROLES = [
  { id: 1, name: 'Admin' },
  { id: 2, name: 'Accountant' },
  { id: 3, name: 'In-house Agent' },
  { id: 4, name: 'Sales Agent' },
  { id: 5, name: 'Leader' },
  { id: 6, name: 'Super Admin' },
  { id: 8, name: 'Group Admin' },
  { id: 9, name: 'Astern Admin' },
  { id: 10, name: 'Referral' },
  { id: 11, name: 'Viewer' },
  { id: 12, name: 'Designer' },
];

// Roles that earn commission. Referral mirrors In-house Agent (same comm
// mechanics) — counts as an agent for buddy/manager assignment, downline
// trees, etc. Group Admin / Astern Admin / Admin / Super Admin are pure-admin
// and don't earn commission, so they're NOT included here. (Multi-role users
// are still picked up because the check is `roleId.some(...)`.)
const AGENT_ROLE_IDS = [3, 4, 5, 10]; // In-house Agent, Sales Agent, Leader, Referral (PSC eligible)
const NON_REFERRAL_AGENT_IDS = [3, 4, 5]; // In-house Agent, Sales Agent, Leader (DOC/TOC eligible — Referral excluded)
// A Referral earns a flat referral fee, NOT personal sales commission. The rate is
// stored in the SAME UserCommission.PersonalComm column (the payout reads it via
// SalesCommissionRepository: FinalPrice × psc%), so the field is shared — only the
// label and the default differ. Confirmed by user 2026-08-13: referral = 2%, and a
// referral must never be shown any other rate. (Previously the field defaulted to
// blank/0 and one referral was created at 5%.)
const REFERRAL_FEE_PCT = 2;

// A percentage field where EMPTY and ZERO mean different things — used by the MUC
// share, where blank is "follow the global setting" and 0 is "this agent keeps none
// of the mark-up". Returns null only for blank/non-numeric. (user 2026-11-02)
const pctOrNull = (v) => {
  if (v === '' || v === null || v === undefined) return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? Math.min(100, Math.max(0, n)) : null;
};

// ---------------------------------------------------------------------------
// Shared style constants
// ---------------------------------------------------------------------------
const PP = "'Poppins', sans-serif";
const SECTION_LABEL_SX = {
  fontSize: '0.75rem', fontWeight: 600, color: '#64748B',
  textTransform: 'uppercase', letterSpacing: '0.5px', mt: 1,
};
const SAVE_BTN_SX = {
  background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)',
  fontFamily: PP,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '8px',
  boxShadow: 'none',
  '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' },
};

export default function AddEditUserModal({ open, onClose, onSaved, user, groups, allUsers = [], fullPage = false }) {
  const { user: currentUser } = useUser();
  const isEdit = !!user;

  const [form, setForm] = useState({
    username: '',
    password: '',
    firstName: '',
    lastName: '',
    alias: '',
    email: '',
    phoneNumber: '',
    icNumber: '',
    incomeTaxNo: '',
    billingName: '',
    companyRegNo: '',
    companyIncomeTaxNo: '',
    companyAddress: '',
    bankName: '',
    bankAccountNumber: '',
    bankBeneficiaryName: '',
    address: '',
    salesTitle: '',
    roleId: [],
    groupId: 0,
    uplineId: null,
    personalComm: 0,
    downlineOverridingComm: null,
    teamOverridingComm: null,
    managerOverridingComm: null,
    // Blank = follow the global MUC share in Settings → Tier. Only filled in for an
    // agent whose mark-up split differs from everyone else's. (user 2026-11-02)
    markupComm: '',
    commissionEffectiveFrom: '',
    commissionRemarks: '',
    status: 'Active',
    processingFeeEnabled: false,
    createSaleApproved: false,
  });

  // â”€â”€ Shared state â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [error, setError] = useState('');       // create-mode / dialog-mode global
  const [loading, setLoading] = useState(false); // create-mode / dialog-mode global

  // â”€â”€ Details card state (fullPage edit mode) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError]     = useState('');
  const [detailsSuccess, setDetailsSuccess] = useState('');

  // â”€â”€ Commission card state (fullPage edit mode) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [commLoading, setCommLoading]   = useState(false);
  const [commError, setCommError]       = useState('');
  const [commSuccess, setCommSuccess]   = useState('');

  // â”€â”€ Team & Settings card state (fullPage edit mode) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const [teamLoading, setTeamLoading]   = useState(false);
  const [teamError, setTeamError]       = useState('');
  const [teamSuccess, setTeamSuccess]   = useState('');

  // Quiz status (Task B10) — read-only summary shown next to the manual
  // "Allow create sales" override. null = loading/not fetched yet;
  // { error: true } = fetch failed (e.g. transient network issue).
  const [quizStatus, setQuizStatus] = useState(null);

  const [commHistory, setCommHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [resetPassword, setResetPassword] = useState('');
  // Buddy state. Each entry: { uplineId: number, splitPercent: number }.
  // Editing single-buddy users looks identical to today's Upline picker.
  const [buddies, setBuddies] = useState([]);
  const [buddyHistory, setBuddyHistory] = useState([]);
  // "Downline of a buddy can't have a buddy" — when the user's single upline (or anyone
  // up its chain) already has a multi-buddy split, "Add Buddy" is greyed out. (user 2026-08-10)
  const [uplineChainSplit, setUplineChainSplit] = useState({ hasSplit: false, blockedBy: '' });
  // Manager assignment — single recipient. {assignmentId, managerId} loaded from active row,
  // saved by expiring the existing row + creating a new one when the picker changes.
  const [currentManager, setCurrentManager] = useState({ assignmentId: null, managerId: 0 });
  const [pickedManagerId, setPickedManagerId] = useState(0);
  // Snapshot of the commission rates + date as LOADED, so a plain info/password
  // save never re-stamps commission. We only send commissionEffectiveFrom to the
  // backend when the admin actually edited a rate or the date (user 2026-06-08).
  const origCommRef = useRef({ personalComm: null, downlineOverridingComm: null, teamOverridingComm: null, managerOverridingComm: null, commissionEffectiveFrom: null });
  // Snapshot of buddies as loaded, so "Save Team & Settings" only calls
  // saveBuddies when the admin actually changed buddy/upline assignments.
  const origBuddiesRef = useRef([]);
  // Tier (Sales Agent only) — Super Admin can manually override here.
  const [tiers, setTiers] = useState([]);
  const [pickedTierId, setPickedTierId] = useState(0);

  // Helper: reload data after a successful save (keeps form in sync for
  // subsequent saves without navigating away).
  const reloadUserData = async () => {
    if (!user) return;
    try {
      const res = await getAgentDetailsByAgentId(user.userId);
      const details = res.data;
      setForm(prev => ({
        ...prev,
        firstName: details.firstName ?? prev.firstName,
        lastName: details.lastName ?? prev.lastName,
        alias: details.alias ?? prev.alias,
        email: details.email ?? prev.email,
        phoneNumber: details.phoneNumber ?? prev.phoneNumber,
        icNumber: details.icNumber ?? prev.icNumber,
        incomeTaxNo: details.incomeTaxNo ?? prev.incomeTaxNo,
        billingName: details.billingName ?? prev.billingName,
        companyRegNo: details.companyRegNo ?? prev.companyRegNo,
        companyIncomeTaxNo: details.companyIncomeTaxNo ?? prev.companyIncomeTaxNo,
        companyAddress: details.companyAddress ?? prev.companyAddress,
        bankName: details.bankName ?? prev.bankName,
        bankAccountNumber: details.bankAccountNumber ?? prev.bankAccountNumber,
        bankBeneficiaryName: details.bankBeneficiaryName ?? prev.bankBeneficiaryName,
        address: details.address ?? prev.address,
        salesTitle: details.salesTitle ?? prev.salesTitle,
        personalComm: details.personalComm ?? prev.personalComm,
        downlineOverridingComm: details.downlineOverridingComm ?? prev.downlineOverridingComm,
        teamOverridingComm: details.teamOverridingComm ?? prev.teamOverridingComm,
        managerOverridingComm: details.managerOverridingComm ?? prev.managerOverridingComm,
        markupComm: details.markupComm ?? '',
        processingFeeEnabled: details.processingFeeEnabled ?? prev.processingFeeEnabled,
        createSaleApproved: details.createSaleApproved ?? prev.createSaleApproved,
      }));
      // Re-snapshot origComm so next commission save compares against fresh rates.
      try {
        const histRes = await getCommissionHistory(user.userId);
        const hist = histRes.data || [];
        setCommHistory(hist);
        const activeRate = hist.find(h => !h.effectiveTo) || hist[hist.length - 1];
        const loadedDate = activeRate?.effectiveFrom ? dayjs(activeRate.effectiveFrom).format('YYYY-MM-DD') : '';
        if (loadedDate) setForm(prev => ({ ...prev, commissionEffectiveFrom: loadedDate }));
        origCommRef.current = {
          personalComm: details.personalComm ?? 0,
          downlineOverridingComm: details.downlineOverridingComm ?? null,
          teamOverridingComm: details.teamOverridingComm ?? null,
          managerOverridingComm: details.managerOverridingComm ?? null,
          markupComm: details.markupComm ?? '',
          commissionEffectiveFrom: loadedDate,
        };
      } catch { /* ignore — history panel just won't refresh */ }
    } catch { /* ignore — form keeps current values */ }
  };

  useEffect(() => {
    if (!open) return;

    // Force-reset transient state on every open so stale rows from a
    // previous edit session can't bleed into the next one. The async
    // load below will replace them with fresh data from the API.
    setBuddies([]);
    setBuddyHistory([]);
    setCurrentManager({ assignmentId: null, managerId: 0 });
    setPickedManagerId(0);
    setError('');
    setDetailsError('');
    setDetailsSuccess('');
    setCommError('');
    setCommSuccess('');
    setTeamError('');
    setTeamSuccess('');
    setResetPassword('');
    setQuizStatus(null);
    origBuddiesRef.current = [];

    if (isEdit && user) {
      const fetchDetails = async () => {
        try {
          const res = await getAgentDetailsByAgentId(user.userId);
          const details = res.data;
          setForm({
            username: user.username ?? '',
            password: '',
            firstName: details.firstName ?? '',
            lastName: details.lastName ?? '',
            alias: details.alias ?? '',
            email: details.email ?? '',
            phoneNumber: details.phoneNumber ?? '',
            icNumber: details.icNumber ?? '',
            incomeTaxNo: details.incomeTaxNo ?? '',
            billingName: details.billingName ?? '',
            companyRegNo: details.companyRegNo ?? '',
            companyIncomeTaxNo: details.companyIncomeTaxNo ?? '',
            companyAddress: details.companyAddress ?? '',
            bankName: details.bankName ?? '',
            bankAccountNumber: details.bankAccountNumber ?? '',
            bankBeneficiaryName: details.bankBeneficiaryName ?? '',
            address: details.address ?? '',
            salesTitle: details.salesTitle ?? '',
            roleId: user.userRoles?.map(r => r.roleId) ?? [],
            groupId: details.groupId ?? 0,
            uplineId: user.uplineId ?? null,
            personalComm: details.personalComm ?? 0,
            downlineOverridingComm: details.downlineOverridingComm ?? null,
            teamOverridingComm: details.teamOverridingComm ?? null,
            managerOverridingComm: details.managerOverridingComm ?? null,
            markupComm: details.markupComm ?? '',
            commissionEffectiveFrom: '',   // blank until set from history (no stale "today")
            commissionRemarks: '',
            status: user.status ?? 'Active',
            processingFeeEnabled: details.processingFeeEnabled ?? false,
            createSaleApproved: details.createSaleApproved ?? false,
          });
          // Quiz status (Task B10) — read-only, admin-only lookup. Fetched
          // alongside the other details calls; a failure here shouldn't block
          // the rest of the modal from loading.
          try {
            const statusRes = await adminUserStatus(user.userId);
            setQuizStatus(statusRes.data ?? { error: true });
          } catch {
            setQuizStatus({ error: true });
          }
          // Pre-select the effective Sales Title (override if set, else the resolved
          // tier/role-based title) so the dropdown shows the real title.
          try {
            const tRes = await getSalesTitle(user.userId);
            if (tRes.data?.title) setForm(prev => ({ ...prev, salesTitle: tRes.data.title }));
          } catch { /* ignore */ }
          // Fetch commission history
          try {
            const histRes = await getCommissionHistory(user.userId);
            const hist = histRes.data || [];
            setCommHistory(hist);
            // Pre-fill the effective date with the CURRENT rate's start date (not
            // today) so re-saving without changing rates keeps the existing date.
            const activeRate = hist.find(h => !h.effectiveTo) || hist[hist.length - 1];
            const loadedDate = activeRate?.effectiveFrom ? dayjs(activeRate.effectiveFrom).format('YYYY-MM-DD') : '';
            if (loadedDate) setForm(prev => ({ ...prev, commissionEffectiveFrom: loadedDate }));
            // Snapshot loaded commission so handleSubmit can tell if it was edited.
            origCommRef.current = {
              personalComm: details.personalComm ?? 0,
              downlineOverridingComm: details.downlineOverridingComm ?? null,
              teamOverridingComm: details.teamOverridingComm ?? null,
              managerOverridingComm: details.managerOverridingComm ?? null,
              markupComm: details.markupComm ?? '',
              commissionEffectiveFrom: loadedDate,
            };
          } catch { setCommHistory([]); }
          // Fetch buddies; carry uplineName so picker has a fallback display even
          // when allUsers is still loading or the upline got filtered out.
          try {
            const active = await getActiveBuddies(user.userId);
            let loadedBuddies;
            if (active && active.length > 0) {
              loadedBuddies = active.map(b => ({
                uplineId: b.uplineId,
                uplineName: b.uplineName ?? '',
                splitPercent: Number(b.splitPercent),
              }));
            } else if (user.uplineId) {
              loadedBuddies = [{ uplineId: user.uplineId, uplineName: user.uplineName ?? '', splitPercent: 100 }];
            } else {
              loadedBuddies = [];
            }
            setBuddies(loadedBuddies);
            // Snapshot for change-detection in "Save Team & Settings".
            origBuddiesRef.current = loadedBuddies.map(b => ({ uplineId: b.uplineId, splitPercent: b.splitPercent }));
          } catch {
            const fallback = user.uplineId ? [{ uplineId: user.uplineId, uplineName: user.uplineName ?? '', splitPercent: 100 }] : [];
            setBuddies(fallback);
            origBuddiesRef.current = fallback.map(b => ({ uplineId: b.uplineId, splitPercent: b.splitPercent }));
          }
          try {
            const hist = await getBuddyHistory(user.userId);
            setBuddyHistory(hist || []);
            // buddyEffectiveFrom intentionally left empty — backend defaults to today
            // when null. Admin fills it only when they want a specific effective date.
          } catch { setBuddyHistory([]); }
          // Active manager (most recent EffectiveTo=null row for this agent)
          try {
            const assignments = await getManagerAssignments();
            const active = (assignments || []).find(a => a.agentId === user.userId && !a.effectiveTo);
            if (active) {
              setCurrentManager({ assignmentId: active.managerAssignmentId, managerId: active.managerId, effectiveFrom: active.effectiveFrom });
              setPickedManagerId(active.managerId);
              // Load the EXISTING effective date. Without this the date field
              // defaults to today, so a later edit could re-stamp the manager
              // assignment to today and shift MOC eligibility forward. We keep
              // the original date unless the admin deliberately changes the
              // manager AND picks a new date.
              setForm(prev => ({ ...prev, managerEffectiveFrom: active.effectiveFrom ? dayjs(active.effectiveFrom).format('YYYY-MM-DD') : '' }));
            } else {
              setCurrentManager({ assignmentId: null, managerId: 0 });
              setPickedManagerId(0);
            }
          } catch {
            setCurrentManager({ assignmentId: null, managerId: 0 });
            setPickedManagerId(0);
          }
          // Tier ladder + this user's current tier (Sales Agent only).
          try {
            const [tiersRes, statusRes] = await Promise.all([
              getTiers(),
              getUserTierStatus(user.userId).catch(() => ({ data: { isEligible: false } })),
            ]);
            setTiers(tiersRes.data || []);
            const tid = statusRes.data?.currentTier?.tierId ?? 0;
            setPickedTierId(tid);
          } catch {
            setTiers([]);
            setPickedTierId(0);
          }
        } catch (err) {
          console.error('Error fetching user details:', err);
        }
      };
      fetchDetails();
    } else {
      setForm({
        username: '',
        password: '',
        firstName: '',
        lastName: '',
        alias: '',
        email: '',
        phoneNumber: '',
        icNumber: '',
        incomeTaxNo: '',
        billingName: '',
        companyRegNo: '',
        companyIncomeTaxNo: '',
        companyAddress: '',
        bankName: '',
        bankAccountNumber: '',
        bankBeneficiaryName: '',
        address: '',
        salesTitle: '',
        roleId: [],
        groupId: 0,
        uplineId: null,
        personalComm: 0,
        downlineOverridingComm: null,
        teamOverridingComm: null,
        managerOverridingComm: null,
        markupComm: '',
        commissionEffectiveFrom: '',
        commissionRemarks: '',
        status: 'Active',
        processingFeeEnabled: false,
        createSaleApproved: false,
      });
      setCommHistory([]);
      setShowHistory(false);
      setBuddies([]);
      setBuddyHistory([]);
      setCurrentManager({ assignmentId: null, managerId: 0 });
      setPickedManagerId(0);
    }
    setError('');
    setResetPassword('');
  }, [open, isEdit, user]);

  // (Removed the auto-reset-to-empty effect — it was clobbering the fresh
  //  row created by clicking "Add Upline" before the user could pick a
  //  value. The save handler already validates that every buddy row has an
  //  uplineId before persisting, so a broken state can't actually be saved.)

  // Buddy helpers — used by the multi-buddy section that replaces the
  // single Upline picker. Adding a new buddy auto-rebalances split equally.
  const addBuddy = () => {
    setBuddies(prev => {
      const split = Number((100 / (prev.length + 1)).toFixed(2));
      return [
        ...prev.map(b => ({ ...b, splitPercent: split })),
        { uplineId: 0, splitPercent: split },
      ];
    });
  };

  const removeBuddy = (idx) => {
    setBuddies(prev => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) return next;
      const split = Number((100 / next.length).toFixed(2));
      return next.map(b => ({ ...b, splitPercent: split }));
    });
  };

  const updateBuddy = (idx, patch) => {
    setBuddies(prev => prev.map((b, i) => i === idx ? { ...b, ...patch } : b));
  };

  const buddySum = buddies.reduce((s, b) => s + (Number(b.splitPercent) || 0), 0);
  const buddySumOk = buddies.length === 0 || Math.abs(buddySum - 100) < 0.01;

  // Grey out "Add Buddy" when the single upline (or anyone up its chain) already has a
  // buddy split — a downline of a buddy can't split again. Only checked while there's one
  // upline picked (before any split). (user 2026-08-10)
  const singleUplineId = buddies.length === 1 ? (Number(buddies[0]?.uplineId) || 0) : 0;
  useEffect(() => {
    if (!singleUplineId) { setUplineChainSplit({ hasSplit: false, blockedBy: '' }); return; }
    let cancelled = false;
    chainHasBuddySplit(singleUplineId)
      .then(r => { if (!cancelled) setUplineChainSplit({ hasSplit: !!r?.hasSplit, blockedBy: r?.blockedBy || '' }); })
      .catch(() => { if (!cancelled) setUplineChainSplit({ hasSplit: false, blockedBy: '' }); });
    return () => { cancelled = true; };
  }, [singleUplineId]);

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    // Clear per-section feedback when the user edits
    setDetailsSuccess('');
    setCommSuccess('');
  };

  const isSuperAdmin = currentUser?.roles?.includes('Super Admin');
  const showCommission = form.roleId.some(id => AGENT_ROLE_IDS.includes(id));
  // Referral role earns only RefComm (= PersonalComm); never DOC/TOC. Hide the inputs
  // for pure-Referral users so the rate stays at 0 and can't be set by mistake.
  const showDOCTOC = form.roleId.some(id => NON_REFERRAL_AGENT_IDS.includes(id));
  // Pure Referral = the Referral role with no other earning role alongside it.
  const isPureReferral = showCommission && !showDOCTOC;

  // Selecting Referral (and nothing else) locks the rate to the flat 2% referral fee,
  // so it can never be typed as an agent PSC by mistake. (user 2026-08-13)
  useEffect(() => {
    if (isPureReferral && Number(form.personalComm) !== REFERRAL_FEE_PCT) {
      setForm(prev => ({ ...prev, personalComm: REFERRAL_FEE_PCT }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPureReferral]);

  // â”€â”€ Shared payload helpers â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

  const getPrimaryUplineId = () => {
    const primaryBuddy = buddies.length > 0
      ? [...buddies].sort((a, b) => (Number(b.splitPercent) || 0) - (Number(a.splitPercent) || 0))[0]
      : null;
    return primaryBuddy?.uplineId || form.uplineId || null;
  };

  // Build the common "full" updateUser payload. The caller overrides only
  // commissionEffectiveFrom / commissionRemarks based on which save it is.
  const buildUpdatePayload = ({ commEffFrom, commRemarks }) => ({
    userId: user.userId,
    firstName: (form.firstName || '').toUpperCase(),
    lastName: (form.lastName || '').toUpperCase(),
    alias: form.alias,
    email: (form.email || '').toLowerCase(),
    phoneNumber: form.phoneNumber,
    icNumber: form.icNumber || null,
    incomeTaxNo: form.incomeTaxNo || null,
    billingName: form.billingName || null,
    companyRegNo: form.companyRegNo || null,
    companyIncomeTaxNo: form.companyIncomeTaxNo || null,
    companyAddress: form.companyAddress || null,
    bankName: form.bankName || null,
    bankAccountNumber: form.bankAccountNumber || null,
    bankBeneficiaryName: form.bankBeneficiaryName || null,
    address: form.address || null,
    salesTitle: form.salesTitle || null,
    uplineId: getPrimaryUplineId(),
    roleId: form.roleId,
    groupId: form.groupId,
    // A pure Referral always submits the flat fee, whatever the form holds — the
    // input is read-only, but this is the gate that actually decides what is stored.
    personalComm: isPureReferral ? REFERRAL_FEE_PCT : (parseFloat(form.personalComm) || 0),
    downlineOverridingComm: showDOCTOC ? (parseFloat(form.downlineOverridingComm) || null) : null,
    teamOverridingComm: showDOCTOC ? (parseFloat(form.teamOverridingComm) || null) : null,
    managerOverridingComm: showDOCTOC && pickedManagerId ? (parseFloat(form.managerOverridingComm) || null) : 0,
    // Blank means "follow the global share", which is NOT the same as 0 ("the agent
    // keeps none of the mark-up") — so this one cannot use the `|| null` idiom above.
    markupComm: pctOrNull(form.markupComm),
    status: form.status,
    commissionEffectiveFrom: commEffFrom,
    commissionRemarks: commRemarks,
    updatedBy: currentUser?.username ?? 'system',
    // Manual create-sales override (Task B10) — independent of the quiz gate.
    CreateSaleApproved: !!form.createSaleApproved,
  });

  // â”€â”€ SAVE DETAILS (edit mode, fullPage, Tab 1) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Sends the full DTO with commission rates held fixed (re-sent as-is from
  // the snapshot) and commissionEffectiveFrom = null so the backend never
  // re-stamps the commission window.
  // Does NOT touch buddies / manager / processingFee — those live in Tab 2.
  // `reactivate` (Task A6): when true, this is the "Reactivate access" button
  // on the Status section, not a plain details save. It piggybacks on the
  // same full-DTO payload but adds `Reactivate: true` so the backend clears
  // the inactivity lock and stamps its own reactivation timestamp — the
  // frontend never sends a timestamp itself.
  const handleSaveDetails = async (reactivate = false) => {
    setDetailsLoading(true);
    setDetailsError('');
    setDetailsSuccess('');
    try {
      // Send commission rates from snapshot (origCommRef) so we never alter them.
      // commissionEffectiveFrom = null → backend leaves existing window untouched.
      const oc = origCommRef.current;
      await updateUser({
        ...buildUpdatePayload({ commEffFrom: null, commRemarks: null }),
        // Override commission fields with the LOADED values (not what may be
        // in the form — the admin was editing the Details card, not Commission).
        personalComm: isPureReferral ? REFERRAL_FEE_PCT : (parseFloat(oc.personalComm) || 0),
        downlineOverridingComm: showDOCTOC ? (parseFloat(oc.downlineOverridingComm) || null) : null,
        teamOverridingComm: showDOCTOC ? (parseFloat(oc.teamOverridingComm) || null) : null,
        managerOverridingComm: showDOCTOC && pickedManagerId ? (parseFloat(oc.managerOverridingComm) || null) : 0,
        markupComm: pctOrNull(oc.markupComm),
        ...(reactivate ? { Reactivate: true } : {}),
      });

      if (resetPassword.trim()) {
        try {
          await adminResetPassword(user.userId, resetPassword);
          setResetPassword('');
        } catch (e) {
          setDetailsError(prev => prev || (e.response?.data ?? 'Failed to reset password'));
        }
      }

      setDetailsSuccess(reactivate ? 'Access reactivated.' : 'Details saved.');
      onSaved();
      // Reload form data so subsequent saves send fresh values.
      await reloadUserData();
    } catch (err) {
      setDetailsError(err.response?.data ?? (reactivate ? 'Failed to reactivate access' : 'An error occurred'));
    } finally {
      setDetailsLoading(false);
    }
  };

  // â”€â”€ SAVE TEAM & SETTINGS (edit mode, fullPage, Tab 2) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Handles buddies (only when changed vs loaded snapshot), manager assignment
  // (only when picker differs from current), and processingFee (Super Admin).
  const handleSaveTeam = async () => {
    if (buddies.length > 0 && !buddySumOk) {
      setTeamError('Buddy split must total 100%.');
      return;
    }
    if (buddies.length > 0 && buddies.some(b => !b.uplineId)) {
      setTeamError('Pick an upline for every buddy row.');
      return;
    }
    setTeamLoading(true);
    setTeamError('');
    setTeamSuccess('');
    let messages = [];
    let hasError = false;

    // Buddy save — only when the current buddies differ from the snapshot at load.
    const currentBuddyStr = JSON.stringify(buddies.map(b => ({ uplineId: b.uplineId, splitPercent: b.splitPercent })));
    const origBuddyStr = JSON.stringify(origBuddiesRef.current);
    const buddiesChanged = currentBuddyStr !== origBuddyStr;
    if (buddiesChanged && buddies.length > 0) {
      try {
        await saveBuddies({
          userId: user.userId,
          buddies: buddies.map(b => ({ uplineId: b.uplineId, splitPercent: Number(b.splitPercent) })),
          // Empty date → backend applies its "Default = today" rule.
          effectiveFrom: form.buddyEffectiveFrom || null,
        });
        messages.push('Upline/buddy saved.');
        // Update snapshot so a second save without further changes is a no-op.
        origBuddiesRef.current = buddies.map(b => ({ uplineId: b.uplineId, splitPercent: b.splitPercent }));
      } catch (e) {
        setTeamError(e.response?.data ?? 'Failed to save buddies');
        hasError = true;
      }
    }

    // Manager assignment: only act when picker differs from current.
    if (!hasError && Number(pickedManagerId || 0) !== Number(currentManager.managerId || 0)) {
      try {
        const effFrom = form.managerEffectiveFrom
          ? new Date(form.managerEffectiveFrom + 'T00:00:00').toISOString()
          : new Date().toISOString();
        if (currentManager.assignmentId) {
          await expireManagerAssignment(currentManager.assignmentId, effFrom);
        }
        if (pickedManagerId && pickedManagerId > 0) {
          await createManagerAssignment({
            managerId: Number(pickedManagerId),
            agentId: user.userId,
            effectiveFrom: effFrom,
          });
        }
        // Update local snapshot so a subsequent save doesn't re-run this.
        setCurrentManager(prev => ({ ...prev, managerId: pickedManagerId }));
        messages.push('Manager assignment saved.');
      } catch (e) {
        setTeamError(prev => prev || (e.response?.data ?? 'Failed to save manager assignment'));
        hasError = true;
      }
    }

    // Processing fee (Super Admin only).
    if (!hasError && isSuperAdmin && showCommission) {
      try {
        const res = await setUserProcessingFee(user.userId, !!form.processingFeeEnabled);
        const n = res.data?.recalculated ?? 0;
        messages.push(`Processing fee ${form.processingFeeEnabled ? 'enabled' : 'disabled'}${n > 0 ? ` — ${n} pending slip(s) recalculated.` : '.'}`);
      } catch (err) {
        setTeamError(prev => prev || (err.response?.data ?? 'Failed to update processing fee'));
        hasError = true;
      }
    }

    if (!hasError) {
      setTeamSuccess(messages.length > 0 ? messages.join(' ') : 'No changes to save.');
      if (messages.length > 0) onSaved();
    }
    setTeamLoading(false);
  };

  // â”€â”€ SAVE COMMISSION (edit mode, fullPage) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // Sends the full DTO with details fields unchanged (from form) but applies
  // the edited commission rates and the proper commissionEffectiveFrom logic.
  const handleSaveCommission = async () => {
    setCommLoading(true);
    setCommError('');
    setCommSuccess('');
    try {
      const oc = origCommRef.current;
      // Determine if admin actually touched a rate or date (same logic as the
      // original handleSubmit commEdited check).
      const commEdited =
        String(form.personalComm ?? '') !== String(oc.personalComm ?? '')
        || String(form.downlineOverridingComm ?? '') !== String(oc.downlineOverridingComm ?? '')
        || String(form.teamOverridingComm ?? '') !== String(oc.teamOverridingComm ?? '')
        || String(form.managerOverridingComm ?? '') !== String(oc.managerOverridingComm ?? '')
        || (form.commissionEffectiveFrom || '') !== (oc.commissionEffectiveFrom || '');

      await updateUser(buildUpdatePayload({
        // Only send the effective date when commission was actually edited;
        // this mirrors the original behaviour exactly.
        commEffFrom: commEdited ? (form.commissionEffectiveFrom || null) : null,
        commRemarks: commEdited ? (form.commissionRemarks || null) : null,
      }));

      setCommSuccess('Commission saved.');
      onSaved();
      // Reload so the Rate History panel and origCommRef snapshot are fresh.
      await reloadUserData();
    } catch (err) {
      setCommError(err.response?.data ?? 'An error occurred');
    } finally {
      setCommLoading(false);
    }
  };

  // â”€â”€ CREATE / dialog-mode single submit â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const handleSubmit = async () => {
    setLoading(true);
    setError('');
    const primaryUplineId = getPrimaryUplineId();
    if (buddies.length > 0 && !buddySumOk) {
      setError('Buddy split must total 100%.');
      setLoading(false);
      return;
    }
    if (buddies.length > 0 && buddies.some(b => !b.uplineId)) {
      setError('Pick an upline for every buddy row.');
      setLoading(false);
      return;
    }
    const fmtFirst = (form.firstName || '').toUpperCase();
    const fmtLast = (form.lastName || '').toUpperCase();
    const fmtEmail = (form.email || '').toLowerCase();
    try {
      if (!form.username || !form.password) {
        setError('Username and password are required.');
        setLoading(false);
        return;
      }
      const created = await createUser({
        username: form.username,
        password: form.password,
        firstName: fmtFirst,
        lastName: fmtLast,
        alias: form.alias,
        email: fmtEmail,
        phoneNumber: form.phoneNumber,
        uplineId: primaryUplineId,
        roleId: form.roleId,
        groupId: form.groupId,
        // A pure Referral always submits the flat fee, whatever the form holds — the
    // input is read-only, but this is the gate that actually decides what is stored.
    personalComm: isPureReferral ? REFERRAL_FEE_PCT : (parseFloat(form.personalComm) || 0),
        downlineOverridingComm: showDOCTOC ? (parseFloat(form.downlineOverridingComm) || null) : null,
        teamOverridingComm: showDOCTOC ? (parseFloat(form.teamOverridingComm) || null) : null,
        managerOverridingComm: showDOCTOC && pickedManagerId ? (parseFloat(form.managerOverridingComm) || null) : 0,
        markupComm: pctOrNull(form.markupComm),
        status: form.status,
        createdBy: currentUser?.username ?? 'system',
      });
      // If buddies were configured, persist them on the new user. The
      // backend backfill creates a 100%-row from uplineId on insert, but
      // multi-buddy splits need an explicit save.
      const newUserId = created?.data?.userId ?? created?.data?.UserId ?? created?.userId;
      if (buddies.length > 1 && newUserId) {
        try {
          await saveBuddies({
            userId: newUserId,
            buddies: buddies.map(b => ({ uplineId: b.uplineId, splitPercent: Number(b.splitPercent) })),
            effectiveFrom: form.buddyEffectiveFrom || null,
          });
        } catch (e) {
          setError(prev => prev || (e.response?.data ?? 'Failed to save buddies'));
        }
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(err.response?.data ?? 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  // â”€â”€ Buddy picker section (shared between both layouts) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const buddySection = (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          {buddies.length > 1 ? 'Buddies / Uplines' : 'Upline'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          {buddies.length > 1 && (
            <Button
              size="small"
              onClick={() => setBuddies([{ uplineId: buddies[0]?.uplineId || 0, splitPercent: 100 }])}
              sx={{ textTransform: 'none', fontFamily: PP, fontSize: '0.7rem', color: '#94A3B8' }}
            >
              Reset to single
            </Button>
          )}
          <Button
            size="small"
            startIcon={<AddIcon />}
            disabled={buddies.length >= 1 && uplineChainSplit.hasSplit}
            onClick={() => buddies.length === 0 ? setBuddies([{ uplineId: 0, splitPercent: 100 }]) : addBuddy()}
            sx={{ textTransform: 'none', fontFamily: PP, fontSize: '0.75rem' }}
          >
            {buddies.length === 0 ? 'Add Upline' : 'Add Buddy'}
          </Button>
        </Box>
      </Box>
      {buddies.length >= 1 && uplineChainSplit.hasSplit && (
        <Typography sx={{ fontSize: '0.7rem', color: '#B45309', fontFamily: PP, mb: 0.75, display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <WarningAmberIcon sx={{ fontSize: '0.9rem' }} />
          Upline {uplineChainSplit.blockedBy ? `(${uplineChainSplit.blockedBy})` : ''} already has a buddy — a downline of a buddy can't have its own buddy.
        </Typography>
      )}
      {buddies.length === 0 && (
        <Typography sx={{ fontSize: '0.75rem', color: '#94A3B8', fontFamily: PP, py: 0.5 }}>
          No upline assigned. Click "Add Upline" to set one.
        </Typography>
      )}
      {buddies.map((b, idx) => (
        <Stack direction="row" spacing={1} key={idx} sx={{ mb: 0.75 }} alignItems="center">
          <FormControl size="small" sx={{ flex: 1 }}>
            <InputLabel>{buddies.length > 1 ? 'Buddy' : 'Upline'}</InputLabel>
            {(() => {
              const isPureAdmin = (u) => {
                const roleNames = (u.userRoles || []).map(r => r.roleName);
                return roleNames.length > 0 && roleNames.every(r => EXCLUDED_BUDDY_ROLES.includes(r));
              };
              const filtered = allUsers.filter(u =>
                u.userId !== user?.userId &&
                u.status === 'Active' &&
                !isPureAdmin(u) &&
                !buddies.some((bb, bbIdx) => bbIdx !== idx && bb.uplineId === u.userId)
              );
              const hasMatch = b.uplineId && filtered.some(u => u.userId === b.uplineId);
              const fallback = b.uplineId && !hasMatch
                ? { userId: b.uplineId, label: b.uplineName || `User #${b.uplineId}` }
                : null;
              return (
                <Select
                  value={b.uplineId || 0}
                  label={buddies.length > 1 ? 'Buddy' : 'Upline'}
                  onChange={(e) => updateBuddy(idx, { uplineId: Number(e.target.value) || 0, uplineName: '' })}
                >
                  <MenuItem value={0}>(pick a buddy)</MenuItem>
                  {fallback && (
                    <MenuItem key={`fallback-${fallback.userId}`} value={fallback.userId}>
                      {fallback.label}
                    </MenuItem>
                  )}
                  {filtered.map(u => (
                    <MenuItem key={u.userId} value={u.userId}>
                      {`${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.alias || ''} {u.groupName ? `(${u.groupName})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              );
            })()}
          </FormControl>
          {buddies.length > 1 && (
            <TextField
              type="number"
              label="Split %"
              size="small"
              value={b.splitPercent}
              onChange={(e) => updateBuddy(idx, { splitPercent: parseFloat(e.target.value) || 0 })}
              inputProps={{ min: 0, max: 100, step: 0.01 }}
              sx={{ width: 110 }}
            />
          )}
          <IconButton size="small" onClick={() => removeBuddy(idx)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Stack>
      ))}
      {buddies.length > 1 && (
        <Typography sx={{ fontSize: '0.72rem', fontFamily: PP, color: buddySumOk ? 'success.main' : 'error.main', mt: 0.5 }}>
          Total split: {buddySum.toFixed(2)}% {buddySumOk ? '(OK)' : '(must equal 100%)'}
        </Typography>
      )}

      {/* Effective-from date for buddy save. */}
      <Box sx={{ mt: 1 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: '#64748B', mb: 0.5, fontFamily: PP }}>
          Buddy change effective from
        </Typography>
        <DatePicker
          value={form.buddyEffectiveFrom ? dayjs(form.buddyEffectiveFrom) : null}
          onChange={(val) => setForm(prev => ({ ...prev, buddyEffectiveFrom: val ? val.format('YYYY-MM-DD') : '' }))}
          slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiOutlinedInput-root': { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } } } }}
        />
        <Typography sx={{ fontSize: '0.7rem', color: '#94A3B8', mt: 0.25, fontFamily: PP }}>
          On save: previous buddies are expired at this date and the new buddies start from this date. Default = today.
        </Typography>
      </Box>

      {/* Buddy history */}
      {isEdit && buddyHistory.length > 0 && (
        <Accordion elevation={0} sx={{ mt: 1, '&:before': { display: 'none' }, background: '#F8FAFC', borderRadius: '6px' }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 36, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
            <Typography sx={{ fontSize: '0.72rem', color: '#64748B', fontFamily: PP }}>
              Buddy history ({buddyHistory.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            {buddyHistory.map(h => (
              <Box key={h.userBuddyId} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                <Typography sx={{ fontSize: '0.7rem', fontFamily: PP }}>
                  {h.uplineName} — {Number(h.splitPercent).toFixed(2)}%
                </Typography>
                <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: PP }}>
                  {new Date(h.effectiveFrom).toLocaleDateString('en-MY')}
                  {h.effectiveTo ? ` → ${new Date(h.effectiveTo).toLocaleDateString('en-MY')}` : ' → active'}
                </Typography>
              </Box>
            ))}
          </AccordionDetails>
        </Accordion>
      )}
    </Box>
  );

  // â”€â”€ Manager section (shared) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const managerSection = (
    <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 1.25 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
          Manager (receives MOC)
        </Typography>
        {pickedManagerId !== (currentManager.managerId || 0) && (
          <Typography sx={{ fontSize: '0.7rem', color: '#F59E0B', fontFamily: PP }}>
            Pending change — applies on Save
          </Typography>
        )}
      </Box>
      <FormControl size="small" fullWidth>
        <InputLabel>Manager</InputLabel>
        <Select
          value={pickedManagerId || 0}
          label="Manager"
          onChange={(e) => setPickedManagerId(Number(e.target.value) || 0)}
        >
          <MenuItem value={0}>(no manager)</MenuItem>
          {allUsers
            .filter(u => {
              if (u.userId === user?.userId) return false;
              if (u.status !== 'Active') return false;
              const roleNames = (u.userRoles || []).map(r => r.roleName);
              const isPureAdmin = roleNames.length > 0 && roleNames.every(r => EXCLUDED_BUDDY_ROLES.includes(r));
              return !isPureAdmin;
            })
            .map(u => (
              <MenuItem key={u.userId} value={u.userId}>
                {`${u.lastName ?? ''} ${u.firstName ?? ''}`.trim() || u.alias || ''} {u.groupName ? `(${u.groupName})` : ''}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
      <Box sx={{ mt: 1.25 }}>
        <Typography sx={{ fontSize: '0.7rem', fontWeight: 500, color: '#64748B', mb: 0.5, fontFamily: PP }}>
          Manager assignment effective from
        </Typography>
        <DatePicker
          value={form.managerEffectiveFrom ? dayjs(form.managerEffectiveFrom) : null}
          disabled={!pickedManagerId}
          onChange={(val) => setForm(prev => ({ ...prev, managerEffectiveFrom: val ? val.format('YYYY-MM-DD') : '' }))}
          slotProps={{ textField: { size: 'small', fullWidth: true, sx: { '& .MuiOutlinedInput-root': { fontFamily: PP, fontSize: '0.85rem', borderRadius: '8px' } } } }}
        />
        <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 0.25, fontFamily: PP }}>
          Sales whose deposit date is on or after this date will pick up MOC. Default = today.
        </Typography>
      </Box>
    </Box>
  );

  // â”€â”€ Commission rates section (shared) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const commissionSection = showCommission ? (
    <>
      <Typography sx={{ ...SECTION_LABEL_SX, mt: 0 }}>
        {isPureReferral ? 'RFC (%)' : 'Commission Rates (%)'}
      </Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        {isPureReferral ? (
          /* Read-only: the referral fee is a fixed 2% of Final Price, not a negotiable
             rate. It was previously editable with only a warning underneath, which is
             exactly how one referrer got created at 5% and was paid nothing for a month
             while their profile advertised an agent's PSC. Every referral is 2%
             (user 2026-08-29). Untick Referral, or add an earning role alongside it, and
             the normal PSC field comes back. */
          <TextField
            label="RFC %"
            type="number"
            value={form.personalComm}
            size="small"
            fullWidth
            InputProps={{ readOnly: true }}
            helperText={`Fixed ${REFERRAL_FEE_PCT}% of Final Price — the standard referral fee, deducted from the agent's PSC.`}
          />
        ) : (
          <TextField label="PSC %" type="number" value={form.personalComm} onChange={handleChange('personalComm')} size="small" fullWidth inputProps={{ min: 0, step: 0.5 }} />
        )}
        {showDOCTOC && (
          <>
            <TextField label="DOC %" type="number" value={form.downlineOverridingComm ?? ''} onChange={handleChange('downlineOverridingComm')} size="small" fullWidth inputProps={{ min: 0, step: 0.5 }} />
            <TextField label="TOC %" type="number" value={form.teamOverridingComm ?? ''} onChange={handleChange('teamOverridingComm')} size="small" fullWidth inputProps={{ min: 0, step: 0.5 }} />
            <TextField
              label="MOC %"
              type="number"
              value={pickedManagerId ? (form.managerOverridingComm ?? '') : 0}
              onChange={handleChange('managerOverridingComm')}
              size="small"
              fullWidth
              disabled={!pickedManagerId}
              helperText={!pickedManagerId ? 'Pick a Manager above to enable' : ' '}
              inputProps={{ min: 0, step: 0.5 }}
            />
            {/* This agent's own MUC processing fee — what the Company keeps from their
                mark-up. Blank for almost everyone, meaning the global fee in Settings →
                Tier applies. Shown as a FEE to match that screen, while the column stores
                the agent's SHARE; this is the only place the two forms meet, and keeping
                the stored form unchanged is what spares a migration of live values.
                NOT part of the 11% sum below: a mark-up is money the customer paid above
                list, not a slice of the commission pot. (user 2026-11-03) */}
            <TextField
              label="MUC fee %"
              type="number"
              value={form.markupComm === '' || form.markupComm == null ? '' : (100 - Number(form.markupComm))}
              onChange={e => setForm(prev => ({
                ...prev,
                markupComm: e.target.value === '' ? '' : String(100 - (parseFloat(e.target.value) || 0)),
              }))}
              size="small"
              fullWidth
              placeholder="global"
              helperText="Blank = global fee"
              inputProps={{ min: 0, max: 100, step: 5 }}
            />
          </>
        )}
      </Box>
      {showDOCTOC && (() => {
        const sum =
          (parseFloat(form.personalComm) || 0) +
          (parseFloat(form.downlineOverridingComm) || 0) +
          (parseFloat(form.teamOverridingComm) || 0) +
          (parseFloat(form.managerOverridingComm) || 0);
        const ok = Math.abs(sum - 11) < 0.005;
        return (
          <Typography variant="body2" sx={{ fontFamily: PP, fontSize: '0.72rem', color: ok ? 'success.main' : 'warning.main', mt: -0.5 }}>
            Sum: {sum.toFixed(2)}% {ok ? '(matches 11%)' : '(should be 11%)'}
          </Typography>
        );
      })()}
      {isEdit && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <DatePicker
              label="Effective From"
              value={form.commissionEffectiveFrom ? dayjs(form.commissionEffectiveFrom) : null}
              onChange={(val) => handleChange('commissionEffectiveFrom')({ target: { value: val ? val.format('YYYY-MM-DD') : '' } })}
              format="DD/MM/YYYY"
              slotProps={{ textField: { size: 'small', fullWidth: true } }}
            />
            <TextField
              label="Remarks"
              value={form.commissionRemarks}
              onChange={handleChange('commissionRemarks')}
              size="small"
              fullWidth
              placeholder="e.g. Promoted to senior"
            />
          </Box>

          {/* Commission Rate History */}
          {commHistory.length > 0 && (
            <Box sx={{ mt: 0.5 }}>
              <Button
                size="small"
                onClick={() => setShowHistory(!showHistory)}
                endIcon={showHistory ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                sx={{ fontFamily: PP, textTransform: 'none', fontSize: '0.75rem', color: '#64748B' }}
              >
                Rate History ({commHistory.length})
              </Button>
              <Collapse in={showHistory}>
                <Box sx={{ mt: 0.5, maxHeight: 200, overflowY: 'auto', border: '1px solid #E2E8F0', borderRadius: '8px', p: 1 }}>
                  {commHistory.map((h, i) => (
                    <Box key={h.historyId || i} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5, borderBottom: i < commHistory.length - 1 ? '1px solid #F1F5F9' : 'none' }}>
                      <Box>
                        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#374151', fontFamily: PP }}>
                          PSC {h.personalComm ?? 0}% · DOC {h.downlineOverridingComm ?? 0}% · TOC {h.teamOverridingComm ?? 0}% · MOC {h.managerOverridingComm ?? 0}%
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: PP }}>
                          {new Date(h.effectiveFrom).toLocaleDateString('en-MY')}
                          {h.effectiveTo ? ` → ${new Date(h.effectiveTo).toLocaleDateString('en-MY')}` : ' → Present'}
                          {h.remarks && <span style={{ color: '#1A8CFF' }}> · {h.remarks}</span>}
                        </Typography>
                      </Box>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94A3B8', fontFamily: PP }}>
                        by {h.changedByName || h.changedBy}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          )}
        </>
      )}
    </>
  ) : null;

  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // FULL-PAGE EDIT MODE — 2-column layout: Details left, Commission + Team right
  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  if (fullPage && isEdit) {
    return (
      <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start', flexWrap: 'wrap' }}>

        {/* â”€â”€ LEFT: Details card â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <Paper
          elevation={0}
          sx={{
            flex: '1 1 480px',
            minWidth: 320,
            p: 3,
            borderRadius: '14px',
            border: '1px solid #E2E8F0',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A', mb: 0.5 }}>
            User Details
          </Typography>

          {detailsError && (
            <Typography color="error" sx={{ fontSize: '0.8rem', fontFamily: PP }}>
              {typeof detailsError === 'string' ? detailsError : JSON.stringify(detailsError)}
            </Typography>
          )}
          {detailsSuccess && (
            <Typography sx={{ fontSize: '0.8rem', color: '#10B981', fontFamily: PP }}>
              {detailsSuccess}
            </Typography>
          )}

          {/* Account */}
          <Typography sx={SECTION_LABEL_SX}>Account</Typography>
          <TextField label="Username" value={form.username} onChange={handleChange('username')} disabled={isEdit} required size="small" />
          {isSuperAdmin && (
            <TextField
              label="Reset Password"
              type="password"
              value={resetPassword}
              onChange={(e) => setResetPassword(e.target.value)}
              size="small"
              placeholder="Leave empty to keep current password"
            />
          )}

          {/* Personal Info */}
          <Typography sx={SECTION_LABEL_SX}>Personal Info</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="First Name (Given Name)" value={form.firstName} onChange={handleChange('firstName')} size="small" fullWidth />
            <TextField label="Last Name (Surname)" value={form.lastName} onChange={handleChange('lastName')} size="small" fullWidth />
          </Box>
          <TextField label="Alias" value={form.alias} onChange={handleChange('alias')} size="small" />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Email" value={form.email} onChange={handleChange('email')} size="small" fullWidth />
            <TextField label="Phone Number" value={form.phoneNumber} onChange={e => setForm(prev => ({ ...prev, phoneNumber: formatPhoneNumber(e.target.value) }))} size="small" fullWidth />
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="IC Number" value={form.icNumber ?? ''} onChange={handleChange('icNumber')} size="small" fullWidth />
            <TextField label="Income Tax No" value={form.incomeTaxNo ?? ''} onChange={handleChange('incomeTaxNo')} size="small" fullWidth placeholder="LHDN tax file number (used on CP58)" />
          </Box>
          <FormControl size="small" fullWidth>
            <InputLabel>Sales Title (display only)</InputLabel>
            <Select label="Sales Title (display only)" value={form.salesTitle ?? ''}
              onChange={e => setForm(prev => ({ ...prev, salesTitle: e.target.value }))}>
              {['Sales & Marketing Director', 'Sales Director', 'Sales Leader', 'Sales Lead', 'Senior Sales', 'Sales Executives'].map(o => (
                <MenuItem key={o} value={o}>{o}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Status */}
          <FormControlLabel
            control={
              <Switch
                checked={form.status === 'Active'}
                onChange={(e) => setForm(prev => ({ ...prev, status: e.target.checked ? 'Active' : 'Inactive' }))}
              />
            }
            label={form.status}
            sx={{ '& .MuiTypography-root': { fontFamily: PP, fontSize: '0.85rem' } }}
          />
          {isEdit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start', mt: -1 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => handleSaveDetails(true)}
                disabled={detailsLoading}
                sx={{ textTransform: 'none', fontFamily: PP, fontSize: '0.75rem', borderRadius: '8px' }}
              >
                {detailsLoading ? 'Saving...' : 'Reactivate access'}
              </Button>
              <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP }}>
                Clears an inactivity lock so a blocked agent can sign in again.
              </Typography>
            </Box>
          )}

          {/* RSC certification quiz status + manual create-sales override (Task B10) */}
          {isEdit && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'flex-start' }}>
              <Typography sx={{ fontSize: '0.8rem', fontFamily: PP, color: quizStatus?.passed ? '#10B981' : '#64748B' }}>
                {quizStatus == null
                  ? 'Quiz: loading...'
                  : quizStatus.error
                    ? 'Quiz: status unavailable'
                    : quizStatus.passed
                      ? `Quiz: Passed — best ${quizStatus.bestScore}, ${quizStatus.attempts} attempt${quizStatus.attempts === 1 ? '' : 's'}`
                      : `Quiz: Not passed yet (${quizStatus.attempts} attempt${quizStatus.attempts === 1 ? '' : 's'})`}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.createSaleApproved}
                    onChange={(e) => setForm(prev => ({ ...prev, createSaleApproved: e.target.checked }))}
                  />
                }
                label="Allow create sales (quiz-approved)"
                sx={{ '& .MuiTypography-root': { fontFamily: PP, fontSize: '0.85rem' } }}
              />
              <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', fontFamily: PP }}>
                Manual override — grants create-sales access regardless of quiz result. Saved with Save Details.
              </Typography>
            </Box>
          )}

          {/* Billing & Bank */}
          <Typography sx={SECTION_LABEL_SX}>Billing & Bank</Typography>
          <TextField
            label="Billing Name (Company / Personal Name)"
            value={form.billingName ?? ''}
            onChange={handleChange('billingName')}
            size="small"
            fullWidth
            placeholder="Used as 'Billing Name' on Maker CSV export"
          />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <BankSelect value={form.bankName ?? ''} onChange={(v) => handleChange('bankName')({ target: { value: v } })} />
            <TextField label="Bank Beneficiary Name" value={form.bankBeneficiaryName ?? ''} onChange={handleChange('bankBeneficiaryName')} size="small" fullWidth helperText="Maker CSV beneficiary (blank = billing name, else full name)" />
            <TextField label="Bank Account No" value={form.bankAccountNumber ?? ''} onChange={handleChange('bankAccountNumber')} size="small" fullWidth />
          </Box>
          <TextField
            label="Address"
            value={form.address ?? ''}
            onChange={handleChange('address')}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />

          {/* Company / SSM */}
          <Typography sx={SECTION_LABEL_SX}>Company / SSM</Typography>
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Company Reg No" value={form.companyRegNo ?? ''} onChange={handleChange('companyRegNo')} size="small" fullWidth placeholder="e.g. PG0537146-P" />
            <TextField label="Company Income Tax No" value={form.companyIncomeTaxNo ?? ''} onChange={handleChange('companyIncomeTaxNo')} size="small" fullWidth placeholder="e.g. C5875970000" />
          </Box>
          <TextField
            label="Company Address"
            value={form.companyAddress ?? ''}
            onChange={handleChange('companyAddress')}
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="Registered company address (used on CP58 in company mode)"
          />

          {/* Role & Group */}
          <Typography sx={SECTION_LABEL_SX}>Role & Group</Typography>
          <FormControl size="small">
            <InputLabel>Role</InputLabel>
            <Select
              multiple
              value={form.roleId}
              onChange={handleChange('roleId')}
              label="Role"
              renderValue={(selected) => selected.map(id => ROLES.find(r => r.id === id)?.name).join(', ')}
            >
              {ROLES.map(role => (
                <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Group</InputLabel>
            <Select value={form.groupId} onChange={handleChange('groupId')} label="Group">
              <MenuItem value={0}>No Group</MenuItem>
              {groups.map(g => (
                <MenuItem key={g.groupId} value={g.groupId}>{g.groupName}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Save Details button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              onClick={() => handleSaveDetails()}
              variant="contained"
              disabled={detailsLoading}
              sx={SAVE_BTN_SX}
            >
              {detailsLoading ? 'Saving...' : 'Save Details'}
            </Button>
          </Box>
        </Paper>

        {/* â”€â”€ RIGHT: Commission card + Team & Settings card stacked â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <Box sx={{ flex: '1 1 380px', minWidth: 280, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Commission Rates card */}
          {showCommission && (
            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: '14px',
                border: '1px solid #E2E8F0',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A', mb: 0.5 }}>
                Commission Rates
              </Typography>

              {commError && (
                <Typography color="error" sx={{ fontSize: '0.8rem', fontFamily: PP }}>
                  {typeof commError === 'string' ? commError : JSON.stringify(commError)}
                </Typography>
              )}
              {commSuccess && (
                <Typography sx={{ fontSize: '0.8rem', color: '#10B981', fontFamily: PP }}>
                  {commSuccess}
                </Typography>
              )}

              {/* Tier picker (Sales Agents only) */}
              {isSuperAdmin && form.roleId.includes(4) && tiers.length > 0 && (
                <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 1.25 }}>
                  <Typography sx={{ ...SECTION_LABEL_SX, mt: 0, mb: 1 }}>Sales Tier</Typography>
                  <FormControl size="small" fullWidth>
                    <InputLabel>Tier</InputLabel>
                    <Select
                      value={pickedTierId || 0}
                      label="Tier"
                      onChange={(e) => {
                        const val = Number(e.target.value) || 0;
                        setPickedTierId(val);
                        const t = tiers.find(x => x.tierId === val);
                        if (t) {
                          setForm(prev => ({
                            ...prev,
                            personalComm: t.pscPercent,
                            downlineOverridingComm: t.docPercent,
                            teamOverridingComm: t.tocPercent,
                          }));
                        }
                      }}
                    >
                      <MenuItem value={0}>(no tier)</MenuItem>
                      {tiers.map(t => (
                        <MenuItem key={t.tierId} value={t.tierId}>
                          {t.name} — PSC {t.pscPercent}% / DOC {t.docPercent}% / TOC {t.tocPercent}% (â‰¥ RM {Number(t.revenueThreshold).toLocaleString()})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {commissionSection}

              {/* Save Commission button */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                <Button
                  onClick={handleSaveCommission}
                  variant="contained"
                  disabled={commLoading}
                  sx={SAVE_BTN_SX}
                >
                  {commLoading ? 'Saving...' : 'Save Commission'}
                </Button>
              </Box>
            </Paper>
          )}

          {/* Team & Settings card */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: '14px',
              border: '1px solid #E2E8F0',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1rem', color: '#0F172A', mb: 0.5 }}>
              Team & Settings
            </Typography>

            {teamError && (
              <Typography color="error" sx={{ fontSize: '0.8rem', fontFamily: PP }}>
                {typeof teamError === 'string' ? teamError : JSON.stringify(teamError)}
              </Typography>
            )}
            {teamSuccess && (
              <Typography sx={{ fontSize: '0.8rem', color: '#10B981', fontFamily: PP }}>
                {teamSuccess}
              </Typography>
            )}

            {/* Upline / Buddy */}
            {buddySection}

            {/* Manager */}
            {managerSection}

            {/* Processing Fee (Super Admin only) */}
            {isSuperAdmin && showCommission && (
              <Box sx={{ mt: 1, p: 1.5, border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!form.processingFeeEnabled}
                      onChange={(e) => setForm(prev => ({ ...prev, processingFeeEnabled: e.target.checked }))}
                    />
                  }
                  label={
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem', fontFamily: PP, fontWeight: 600 }}>
                        Processing Fee Deduction
                      </Typography>
                      <Typography sx={{ fontSize: '0.72rem', fontFamily: PP, color: '#64748B' }}>
                        Deduct a 12%/13% processing fee from this agent's commission slip (EPF/SOCSO settlement).
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            )}

            {/* Save Team & Settings button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
              <Button
                onClick={handleSaveTeam}
                variant="contained"
                disabled={teamLoading}
                sx={SAVE_BTN_SX}
              >
                {teamLoading ? 'Saving...' : 'Save Team & Settings'}
              </Button>
            </Box>
          </Paper>

        </Box>
      </Box>
    );
  }

  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  // DIALOG / CREATE MODE — single-card layout, single "Create User" button
  // (also used for edit via Dialog if fullPage=false — kept for backward compat)
  // â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
  const formContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: fullPage ? 0 : '8px !important' }}>
      {error && (
        <Typography color="error" sx={{ fontSize: '0.8rem', fontFamily: PP }}>
          {typeof error === 'string' ? error : JSON.stringify(error)}
        </Typography>
      )}

      <Typography sx={SECTION_LABEL_SX}>Account</Typography>
      <TextField label="Username" value={form.username} onChange={handleChange('username')} disabled={isEdit} required size="small" />
      {!isEdit && (
        <TextField label="Password" type="password" value={form.password} onChange={handleChange('password')} required size="small" />
      )}
      {isEdit && isSuperAdmin && (
        <TextField
          label="Reset Password"
          type="password"
          value={resetPassword}
          onChange={(e) => setResetPassword(e.target.value)}
          size="small"
          placeholder="Leave empty to keep current password"
        />
      )}

      <Typography sx={SECTION_LABEL_SX}>Personal Info</Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField label="First Name (Given Name)" value={form.firstName} onChange={handleChange('firstName')} size="small" fullWidth />
        <TextField label="Last Name (Surname)" value={form.lastName} onChange={handleChange('lastName')} size="small" fullWidth />
      </Box>
      <TextField label="Alias" value={form.alias} onChange={handleChange('alias')} size="small" />
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField label="Email" value={form.email} onChange={handleChange('email')} size="small" fullWidth />
        <TextField label="Phone Number" value={form.phoneNumber} onChange={e => setForm(prev => ({ ...prev, phoneNumber: formatPhoneNumber(e.target.value) }))} size="small" fullWidth />
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField label="IC Number" value={form.icNumber ?? ''} onChange={handleChange('icNumber')} size="small" fullWidth />
        <TextField label="Income Tax No" value={form.incomeTaxNo ?? ''} onChange={handleChange('incomeTaxNo')} size="small" fullWidth placeholder="LHDN tax file number (used on CP58)" />
      </Box>
      <FormControl size="small" fullWidth>
        <InputLabel>Sales Title (display only)</InputLabel>
        <Select label="Sales Title (display only)" value={form.salesTitle ?? ''}
          onChange={e => setForm(prev => ({ ...prev, salesTitle: e.target.value }))}>
          {['Sales & Marketing Director', 'Sales Director', 'Sales Leader', 'Sales Lead', 'Senior Sales', 'Sales Executives'].map(o => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </Select>
      </FormControl>

      <Typography sx={SECTION_LABEL_SX}>Billing & Bank</Typography>
      <TextField
        label="Billing Name (Company / Personal Name)"
        value={form.billingName ?? ''}
        onChange={handleChange('billingName')}
        size="small"
        fullWidth
        placeholder="Used as 'Billing Name' on Maker CSV export"
      />
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <BankSelect value={form.bankName ?? ''} onChange={(v) => handleChange('bankName')({ target: { value: v } })} />
        <TextField label="Bank Beneficiary Name" value={form.bankBeneficiaryName ?? ''} onChange={handleChange('bankBeneficiaryName')} size="small" fullWidth helperText="Maker CSV beneficiary (blank = billing name, else full name)" />
        <TextField label="Bank Account No" value={form.bankAccountNumber ?? ''} onChange={handleChange('bankAccountNumber')} size="small" fullWidth />
      </Box>
      <TextField label="Address" value={form.address ?? ''} onChange={handleChange('address')} size="small" fullWidth multiline minRows={2} />

      <Typography sx={SECTION_LABEL_SX}>Company / SSM</Typography>
      <Box sx={{ display: 'flex', gap: 1.5 }}>
        <TextField label="Company Reg No" value={form.companyRegNo ?? ''} onChange={handleChange('companyRegNo')} size="small" fullWidth placeholder="e.g. PG0537146-P" />
        <TextField label="Company Income Tax No" value={form.companyIncomeTaxNo ?? ''} onChange={handleChange('companyIncomeTaxNo')} size="small" fullWidth placeholder="e.g. C5875970000" />
      </Box>
      <TextField
        label="Company Address"
        value={form.companyAddress ?? ''}
        onChange={handleChange('companyAddress')}
        size="small"
        fullWidth
        multiline
        minRows={2}
        placeholder="Registered company address (used on CP58 in company mode)"
      />

      <Typography sx={SECTION_LABEL_SX}>Role & Group</Typography>
      <FormControl size="small">
        <InputLabel>Role</InputLabel>
        <Select
          multiple
          value={form.roleId}
          onChange={handleChange('roleId')}
          label="Role"
          renderValue={(selected) => selected.map(id => ROLES.find(r => r.id === id)?.name).join(', ')}
        >
          {ROLES.map(role => (
            <MenuItem key={role.id} value={role.id}>{role.name}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small">
        <InputLabel>Group</InputLabel>
        <Select value={form.groupId} onChange={handleChange('groupId')} label="Group">
          <MenuItem value={0}>No Group</MenuItem>
          {groups.map(g => (
            <MenuItem key={g.groupId} value={g.groupId}>{g.groupName}</MenuItem>
          ))}
        </Select>
      </FormControl>

      {buddySection}
      {managerSection}

      {isEdit && isSuperAdmin && showCommission && (
        <Box sx={{ mt: 1, p: 1.5, border: '1px solid #E2E8F0', borderRadius: '8px' }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!form.processingFeeEnabled}
                onChange={(e) => setForm(prev => ({ ...prev, processingFeeEnabled: e.target.checked }))}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontSize: '0.85rem', fontFamily: PP, fontWeight: 600 }}>
                  Processing Fee Deduction
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontFamily: PP, color: '#64748B' }}>
                  Deduct a 12%/13% processing fee from this agent's commission slip (EPF/SOCSO settlement).
                </Typography>
              </Box>
            }
          />
        </Box>
      )}

      {isEdit && isSuperAdmin && form.roleId.includes(4) && tiers.length > 0 && (
        <Box sx={{ border: '1px solid #E2E8F0', borderRadius: '8px', p: 1.25, mt: 1 }}>
          <Typography sx={{ ...SECTION_LABEL_SX, mt: 0 }}>Sales Tier</Typography>
          <FormControl size="small" fullWidth>
            <InputLabel>Tier</InputLabel>
            <Select
              value={pickedTierId || 0}
              label="Tier"
              onChange={(e) => {
                const val = Number(e.target.value) || 0;
                setPickedTierId(val);
                const t = tiers.find(x => x.tierId === val);
                if (t) {
                  setForm(prev => ({
                    ...prev,
                    personalComm: t.pscPercent,
                    downlineOverridingComm: t.docPercent,
                    teamOverridingComm: t.tocPercent,
                  }));
                }
              }}
            >
              <MenuItem value={0}>(no tier)</MenuItem>
              {tiers.map(t => (
                <MenuItem key={t.tierId} value={t.tierId}>
                  {t.name} — PSC {t.pscPercent}% / DOC {t.docPercent}% / TOC {t.tocPercent}% (â‰¥ RM {Number(t.revenueThreshold).toLocaleString()})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      {commissionSection}

      <FormControlLabel
        control={
          <Switch
            checked={form.status === 'Active'}
            onChange={(e) => setForm(prev => ({ ...prev, status: e.target.checked ? 'Active' : 'Inactive' }))}
          />
        }
        label={form.status}
        sx={{ mt: 1, '& .MuiTypography-root': { fontFamily: PP, fontSize: '0.85rem' } }}
      />
    </Box>
  );

  const actionButtons = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 0, p: 2 }}>
      <Button onClick={onClose} sx={{ fontFamily: PP, textTransform: 'none' }}>Cancel</Button>
      <Button
        onClick={handleSubmit}
        variant="contained"
        disabled={loading}
        sx={SAVE_BTN_SX}
      >
        {loading ? 'Saving...' : 'Create User'}
      </Button>
    </Box>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
      <DialogTitle sx={{ fontFamily: PP, fontWeight: 700, fontSize: '1.1rem' }}>
        Add New User
      </DialogTitle>
      <DialogContent sx={{ pt: '8px !important' }}>
        {formContent}
      </DialogContent>
      {actionButtons}
    </Dialog>
  );
}
